// The chart's visible x window: what a range preset shows, which window a
// redraw keeps, and what a share URL records. Pure, DOM-free; src/chart.ts
// applies the result to uPlot.
//
// The plot's x scale is in display seconds: each period start plus `shift`,
// half a period, so a quarter is drawn at its middle (lib/transforms
// periodMidShiftSec). Dates are ISO period starts. "Raw" means unshifted.

export interface XWindow { min: number; max: number }
/** uPlot's x scale; min/max are unset before the first draw. */
export interface XScale { min?: number | null; max?: number | null }

const YEAR = 365.25 * 86400;

/** state.range is "all", a preset in years ("1", "5", "10") or "custom". */
export const isRangePreset = (r: string | null | undefined): r is string => !!r && /^\d+$/.test(r);

// First and last date in display seconds.
function span(dates: string[], shift: number): [number, number] {
  return [Date.parse(dates[0] as string) / 1000 + shift, Date.parse(dates[dates.length - 1] as string) / 1000 + shift];
}

/** The window a range shows: the last N years of `dates`, or all of them.
 * null for "custom" (a drag-zoom is left as it is) and when there are no dates. */
export function presetWindow(dates: string[], shift: number, range: string | null | undefined): XWindow | null {
  if (!dates.length) return null;
  const r = range || "all";
  if (r !== "all" && !isRangePreset(r)) return null;
  const [minFull, maxT] = span(dates, shift);
  return { min: r === "all" ? minFull : Math.max(minFull, maxT - parseInt(r, 10) * YEAR), max: maxT };
}

/** A drag-zoom to carry over a redraw: the window, when it was narrower than
 * the lines it was drawn on (`prevDates`). Judged against those lines, not the
 * new ones: the auto-fitted full range of a 1990+ selection is not a zoom, and
 * must not clip a 1930+ line that joins it. Never under a range preset, which
 * is re-applied to the new lines instead. */
export function keptZoom(scale: XScale | null, prevDates: string[] | null, shift: number,
  range: string | null | undefined): XWindow | null {
  if (isRangePreset(range) || !scale || scale.min == null || scale.max == null || !prevDates || !prevDates.length) return null;
  const [pMin, pMax] = span(prevDates, shift);
  return scale.min > pMin + 1 || scale.max < pMax - 1 ? { min: scale.min, max: scale.max } : null;
}

export interface Redraw {
  /** The x scale before the redraw, null without a plot. */
  prevScale: XScale | null;
  /** The dates the previous window was drawn on; null after a dataset switch. */
  prevDates: string[] | null;
  /** The new lines' dates. */
  dates: string[];
  shift: number;
  range: string | null | undefined;
  /** A window from a shared URL, applied once. */
  pending: XWindow | null;
}

/** The window to show after a redraw, or null to leave the plot fitted to the
 * data. A pending URL window wins, then a kept drag-zoom, then the preset. */
export function windowAfterDraw(o: Redraw): XWindow | null {
  if (o.pending) return o.pending;
  const kept = keptZoom(o.prevScale, o.prevDates, o.shift, o.range);
  if (kept) return kept;
  return isRangePreset(o.range) ? presetWindow(o.dates, o.shift, o.range) : null;
}

/** The visible window as raw epoch seconds for a share URL, or null when it
 * shows all of `dates` (full-span links stay clean). */
export function zoomedRaw(scale: XScale | null, dates: string[], shift: number): [number, number] | null {
  if (!scale || scale.min == null || scale.max == null || !dates.length) return null;
  const [fullMin, fullMax] = span(dates, 0);
  const xmin = scale.min - shift, xmax = scale.max - shift;
  return xmin > fullMin + 1 || xmax < fullMax - 1 ? [xmin, xmax] : null;
}
